<template>
  <div class="authorization-container">
    <div class="auth-header">
      <div class="header-content">
        <button @click.stop="goBack" class="back-button">
          <span class="back-icon">←</span>
          <span>返回</span>
        </button>
        <div class="header-title">
          <div class="title-icon">🔐</div>
          <h1>账号授权</h1>
        </div>
        <button @click.stop="handleLogout" class="logout-button">
          <span class="logout-icon">🚪</span>
          <span>退出登录</span>
        </button>
      </div>
    </div>

    <div class="auth-content">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-section">
        <div class="loading-spinner"></div>
        <p>正在加载店铺列表...</p>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="error" class="error-section">
        <p class="error-message">{{ error }}</p>
        <button class="retry-button" @click="fetchShopList">重试</button>
      </div>

      <!-- 店铺列表 -->
      <div v-else class="shops-section">
        <h2 class="section-title">已授权店铺</h2>
        <div class="shops-grid">
          <div 
            v-for="shop in shops" 
            :key="shop.id" 
            class="shop-card"
          >
            <button
              class="delete-shop-button"
              @click.stop="deleteShop(shop)"
            >
              删除
            </button>
            <div class="shop-header">
              <span class="shop-icon">🏪</span>
              <h3 class="shop-name">{{ shop.advertiserName || '未命名店铺' }}</h3>
            </div>
            
            <p class="shop-description">店铺ID: {{ shop.userName }}</p>
            
            <div class="shop-status">
              <span class="status-badge authorized">✓ 已授权</span>
            </div>
            
            <!-- 上传视频按钮 -->
            <div class="action-buttons">
              <button 
                class="upload-button"
                @click="goToUploadVideo(shop)"
              >
                <span class="upload-icon">📹</span>
                <span>上传视频</span>
              </button>
              <button 
                class="share-button"
                @click="goToVideoShare(shop)"
              >
                <span class="share-icon">🔁</span>
                <span>视频分享</span>
              </button>
              <button
                class="trace-button"
                @click="goToVideoTrace(shop)"
              >
                <span class="trace-icon">🔎</span>
                <span>视频溯源</span>
              </button>
            </div>
          </div>

          <!-- 新增授权卡片 -->
          <div class="shop-card add-shop-card" @click="handleAuthRedirect('new')">
            <div class="add-shop-content">
              <span class="add-icon">+</span>
              <h3>新增授权</h3>
              <p>点击添加新的店铺授权</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 分隔线 -->
      <div class="divider"></div>

      <div class="auth-intro">
        <h2 class="intro-title">绑定您的电商平台账号</h2>
        <p class="intro-subtitle">授权后，您可以实现批量视频发布和管理</p>
      </div>

      <div class="auth-notice">
        <div class="notice-icon">💡</div>
        <div class="notice-content">
          <h4>授权说明</h4>
          <ul class="notice-list">
            <li>授权过程采用官方API，确保账号安全</li>
            <li>授权后仅用于视频发布和管理功能</li>  
          </ul>
        </div>
      </div>
    </div>

    <div class="background-animation">
      <div class="tech-lines"></div>
      <div class="floating-element element-1"></div>
      <div class="floating-element element-2"></div>
      <div class="floating-element element-3"></div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import API_CONFIG from '@/api/config'

export default {
  name: 'AuthorizationPage',
  data() {
    return {
      shops: [],
      loading: false,
      error: null,
      refreshTimer: null,
      remainAuthorizedCount: 0
    }
  },
  created() {
    this.remainAuthorizedCount = Number(localStorage.getItem('remainAuthorizedCount') || 0)
    this.handleAuthCode();
  },
  methods: {
    toSafeHeaderValue(value) {
      const str = String(value ?? '')
      for (let i = 0; i < str.length; i += 1) {
        if (str.charCodeAt(i) > 255) {
          return encodeURIComponent(str)
        }
      }
      return str
    },
    goBack() {
      this.$router.push('/home');
    },
    handleAuth(platform) {
      platform.authorizing = true;
      
      // 模拟授权过程
      setTimeout(() => {
        platform.authorized = true;
        platform.authorizing = false;
        this.$message.success(`${platform.name} 授权成功！`);
      }, 2000);
    },
    
    // 获取店铺列表
    async fetchShopList() {
      this.loading = true;
      this.error = null;
      
      try {
        const token = localStorage.getItem('authToken');
        const cardNumber = localStorage.getItem('cardNumber');
        if (!token || !cardNumber) {
          this.error = '请先完成授权';
          this.loading = false;
          return;
        }
        
        console.log('请求店铺列表:', {
          url: API_CONFIG.getShopListUrl(),
          params: { cardNumber },
          hasToken: !!token,
          hasCardNumber: !!cardNumber
        });
        
        const safeToken = this.toSafeHeaderValue(token)
        const response = await axios.get(API_CONFIG.getShopListUrl(), {
          params: {
            cardNumber: cardNumber
          },
          headers: {
            Authorization: `Bearer ${safeToken}`
          }
        });
        
        console.log('店铺列表响应:', response.data);
        
        if (response.data.code === 200) {
          this.shops = response.data.data || [];
        } else {
          this.error = response.data.msg || '获取店铺列表失败';
        }
      } catch (error) {
        console.error('获取店铺列表失败:', error);
        console.error('错误详情:', error.response?.data);
        this.error = error.response?.data?.msg || '网络请求失败';
      } finally {
        this.loading = false;
      }
    },
    
    // 处理授权跳转
    handleAuthRedirect() {
      if (this.remainAuthorizedCount <= 0) {
        const cardNumber = localStorage.getItem('cardNumber') || ''
        const renewLink = `http://wxsq.xxdazz.com?card_number=${encodeURIComponent(cardNumber)}`
        const confirmed = window.confirm('授权店铺数量已达到最高，点击“确定”进行续费。')
        if (confirmed) {
          window.open(renewLink, '_blank')
        }
        return
      }
      const randomString = Math.random().toString(36).substring(2, 10);
      const appId = "1864687832647065";
      const materialAuth = "1";
      const rid = randomString;
      const cardNumber = localStorage.getItem('cardNumber');
      const authUrl = `https://qianchuan.jinritemai.com/openapi/qc/audit/oauth.html?app_id=${appId}&material_auth=${materialAuth}&state=${cardNumber}&comment_auth=1&rid=${rid}`;
      console.log('授权跳转...',authUrl);
      window.open(authUrl, '_blank');
    },
    
    async handleAuthCode() {
      console.log('处理授权跳转...',window.location);
      const urlParams = new URLSearchParams(window.location.search);
     
      const authCode = urlParams.get('auth_code'); 
      const cardNumber = urlParams.get('state');
       console.log('处理授权跳转...',authCode,"cardNumber:",cardNumber);
      if (authCode && cardNumber) {
        try {
          // 调用后端接口获取accessToken（GET请求，使用统一标准）
          const response = await axios.get(API_CONFIG.getAccessTokenByCodeUrl(), {
            params: {
              auth_code: authCode,
              cardNumber: cardNumber
            }
          });
          
          if (response.data.code === 200) {
            // 清除URL参数并关闭当前标签页
            window.history.replaceState({}, document.title, window.location.pathname);
            window.close();
          } else {
            console.error('获取accessToken失败:', response.data.message);
          }
        } catch (error) {
          console.error('调用API失败:', error);
          if (error.response) {
            console.error('错误响应:', error.response.data);
          }
        }
      }
    },
    // 退出登录
    handleLogout() {
      // 清除本地存储的认证信息
      localStorage.removeItem('authToken');
      localStorage.removeItem('cardNumber');
      
      // 显示退出成功消息
      alert('退出登录成功');
      
      // 立即跳转到登录页面
      this.$router.push('/');
    },
    
    // 跳转到上传视频页面
    goToUploadVideo(shop) {
      // 存储accessToken到localStorage
      const username = shop.userName || shop.username || ''
      localStorage.setItem('accessToken', shop.accessToken);
      localStorage.setItem('advertiserName', shop.advertiserName);
      localStorage.setItem('username', username);
      
      const routeData = this.$router.resolve({
        path: '/upload-video',
        query: {
          accessToken: shop.accessToken,
          advertiserName: shop.advertiserName,
          username
        }
      });
      window.open(routeData.href, '_blank');
    },
    goToVideoShare(shop) {
      const username = shop.userName || shop.username || ''
      localStorage.setItem('accessToken', shop.accessToken);
      localStorage.setItem('advertiserName', shop.advertiserName);
      localStorage.setItem('username', username);
      localStorage.setItem('sourceAdvertiserId', shop.userName || '');
      
      const routeData = this.$router.resolve({
        path: '/video-share',
        query: {
          accessToken: shop.accessToken,
          advertiserName: shop.advertiserName,
          username,
          sourceAdvertiserId: shop.userName || ''
        }
      });
      window.open(routeData.href, '_blank');
    },
    goToVideoTrace(shop) {
      const username = shop.userName || shop.username || ''
      localStorage.setItem('accessToken', shop.accessToken)
      localStorage.setItem('advertiserName', shop.advertiserName)
      localStorage.setItem('username', username)

      const routeData = this.$router.resolve({
        path: '/video-trace',
        query: {
          accessToken: shop.accessToken,
          advertiserName: shop.advertiserName,
          username,
          sourceAdvertiserId: shop.userName || ''
        }
      })
      window.open(routeData.href, '_blank')
    },
    async deleteShop(shop) {
      const username = shop.userName || shop.username;
      if (!username) {
        alert('店铺账号缺失，无法删除');
        return;
      }
      const confirmed = window.confirm(`确认删除店铺 ${shop.advertiserName || username} 吗？`);
      if (!confirmed) {
        return;
      }
      try {
        const token = localStorage.getItem('authToken');
        const safeToken = this.toSafeHeaderValue(token)
        const response = await axios.get(API_CONFIG.delShop(), {
          params: {
            username
          },
          headers: {
            Authorization: `Bearer ${safeToken}`
          }
        });
        if (response.data.code === 200) {
          await this.fetchShopList();
          alert('删除成功');
        } else {
          alert(response.data.msg || '删除失败');
        }
      } catch (error) {
        console.error('删除店铺失败:', error);
        alert(error.response?.data?.msg || '删除失败');
      }
    }
  },
  
  mounted() {
    this.fetchShopList();
    // 设置10秒刷新一次的定时器
    this.refreshTimer = setInterval(() => {
      this.fetchShopList();
    }, 10000);
  },
  
  beforeDestroy() {
    // 清除定时器，防止内存泄漏
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  },
}
</script>

<style scoped>
.authorization-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  position: relative;
  overflow: hidden;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}.auth-header {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 20px 0;
  position: relative;
  z-index: 10;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 20px;
}

.back-button {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 25px;
  padding: 10px 20px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateX(-5px);
}

.back-icon {
  font-size: 16px;
}

/* 退出按钮样式 */
.logout-button {
  background: rgba(255, 107, 107, 0.2);
  border: 1px solid rgba(255, 107, 107, 0.4);
  border-radius: 25px;
  padding: 10px 20px;
  color: #ff6b6b;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.logout-button:hover {
  background: rgba(255, 107, 107, 0.3);
  transform: translateX(5px);
  color: #ff5252;
}

.logout-icon {
  font-size: 16px;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  font-size: 24px;
}

.header-title h1 {
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.auth-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  position: relative;
  z-index: 10;
}

/* 加载状态 */
.loading-section {
  text-align: center;
  padding: 60px 0;
  color: white;
}

.loading-section .loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

.loading-section p {
  margin: 0;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
}

/* 错误状态 */
.error-section {
  text-align: center;
  padding: 60px 0;
  color: white;
}

.error-message {
  background: rgba(255, 0, 0, 0.2);
  padding: 15px 20px;
  border-radius: 10px;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 0, 0, 0.3);
}

.retry-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 10px 25px;
  border-radius: 20px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.retry-button:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

/* 店铺列表 */
.shops-section {
  margin-bottom: 50px;
}

.section-title {
  color: white;
  font-size: 28px;
  font-weight: 700;
  text-align: center;
  margin: 0 0 30px 0;
  background: linear-gradient(45deg, #00d4ff, #667eea);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.shops-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.shop-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 20px;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.delete-shop-button {
  position: absolute;
  top: 12px;
  right: 12px;
  border: none;
  border-radius: 14px;
  padding: 4px 10px;
  font-size: 12px;
  color: white;
  background: linear-gradient(45deg, #ff6b6b, #ee5253);
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 2;
}

.delete-shop-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(238, 82, 83, 0.45);
}

.shop-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  border-color: rgba(102, 126, 234, 0.5);
}

.add-shop-card {
  background: rgba(255, 255, 255, 0.08);
  border: 2px dashed rgba(102, 126, 234, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 150px;
  text-align: center;
}

.add-shop-card:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(102, 126, 234, 0.8);
}

.add-shop-content {
  color: #667eea;
}

.add-icon {
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 10px;
  display: block;
}

.add-shop-content h3 {
  color: white;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.add-shop-content p {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin: 0;
}

.shop-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}

.shop-icon {
  font-size: 24px;
  background: linear-gradient(45deg, #ff6b6b, #feca57);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.shop-name {
  color: white;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.shop-description {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin: 0 0 15px 0;
}

.shop-status {
  text-align: center;
}

.status-badge {
  background: linear-gradient(45deg, #2ed573, #1dd1a1);
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  display: inline-block;
  margin-bottom: 15px;
}

/* 上传视频按钮样式 */
.upload-button {
  background: linear-gradient(45deg, #667eea, #764ba2);
  border: none;
  border-radius: 25px;
  padding: 12px 20px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
}

.action-buttons {
  display: flex;
  gap: 10px;
  margin-top: 15px;
}

.upload-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
}

.upload-icon {
  font-size: 16px;
}

.share-button {
  background: linear-gradient(45deg, #26c6da, #00acc1);
  border: none;
  border-radius: 25px;
  padding: 12px 20px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
}

.share-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(38, 198, 218, 0.4);
}

.share-icon {
  font-size: 16px;
}

.trace-button {
  background: linear-gradient(45deg, #20bf6b, #0fb9b1);
  border: none;
  border-radius: 25px;
  padding: 12px 20px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
}

.trace-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(15, 185, 177, 0.4);
}

.trace-icon {
  font-size: 16px;
}

/* 分隔线 */
.divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  margin: 40px 0;
}

.auth-intro {
  text-align: center;
  margin-bottom: 50px;
}

.intro-title {
  color: white;
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 15px 0;
  background: linear-gradient(45deg, #00d4ff, #667eea);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.intro-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  margin: 0;
}

.platform-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
  margin-bottom: 40px;
}

.platform-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 25px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.platform-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  border-color: rgba(102, 126, 234, 0.5);
}

.platform-header {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 20px;
}

.platform-icon {
  font-size: 32px;
  background: linear-gradient(45deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.platform-info {
  flex: 1;
}

.platform-name {
  color: white;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 5px 0;
}

.platform-desc {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin: 0;
}

.platform-status {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.platform-status.authorized {
  background: rgba(46, 213, 115, 0.2);
  color: #2ed573;
}

.platform-status:not(.authorized) {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.5);
}

.platform-features {
  margin-bottom: 20px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
}

.feature-dot {
  color: #667eea;
  font-weight: bold;
}

.auth-button {
  width: 100%;
  padding: 12px;
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.auth-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(102, 126, 234, 0.3);
}

.auth-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.auth-button.authorized {
  background: linear-gradient(45deg, #2ed573, #1dd1a1);
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.auth-notice {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 25px;
  display: flex;
  gap: 15px;
  max-width: 800px;
  margin: 0 auto;
}

.notice-icon {
  font-size: 24px;
  color: #00d4ff;
  flex-shrink: 0;
}

.notice-content h4 {
  color: white;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 10px 0;
}

.notice-list {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
  padding-left: 20px;
}

.notice-list li {
  margin-bottom: 5px;
}

.background-animation {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.tech-lines {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: 
    linear-gradient(90deg, transparent 49%, rgba(102, 126, 234, 0.1) 50%, transparent 51%),
    linear-gradient(0deg, transparent 49%, rgba(102, 126, 234, 0.1) 50%, transparent 51%);
  background-size: 60px 60px;
  animation: tech-move 15s linear infinite;
}

@keyframes tech-move {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(60px, 60px);
  }
}

.floating-element {
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%);
  animation: float 10s infinite ease-in-out;
}

.element-1 {
  width: 60px;
  height: 60px;
  top: 25%;
  left: 15%;
  animation-delay: 0s;
}

.element-2 {
  width: 90px;
  height: 90px;
  top: 65%;
  right: 25%;
  animation-delay: 4s;
}

.element-3 {
  width: 45px;
  height: 45px;
  bottom: 25%;
  left: 75%;
  animation-delay: 8s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-25px) rotate(180deg);
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 15px;
  }
  
  .intro-title {
    font-size: 28px;
  }
  
  .platform-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .auth-notice {
    flex-direction: column;
    text-align: center;
  }
  
  .notice-list {
    text-align: left;
  }
}

@media (max-width: 480px) {
  .platform-card {
    padding: 20px;
  }
  
  .platform-header {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }
  
  .intro-title {
    font-size: 24px;
  }
}
</style>
